import { formatAppDate } from "../../utils/formatters";
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../AppIcon';
import useNotifications from '../../hooks/useNotifications';

const NotificationPanel = ({ user, onNewNotification }) => {
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);
  const panelRef = useRef(null);
  const navigate = useNavigate();

  const { 
    notifications, 
    unreadCount, 
    markAsRead, 
    markAllAsRead, 
    dismissNotification 
  } = useNotifications(user?.id, onNewNotification);

  // Helper to format date to relative time (e.g. "5 mins ago")
  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return t('time.just_now');
    if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return t(minutes === 1 ? 'time.minute_ago' : 'time.minutes_ago', { count: minutes });
    }
    if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return t(hours === 1 ? 'time.hour_ago' : 'time.hours_ago', { count: hours });
    }
    if (diffInSeconds < 604800) {
      const days = Math.floor(diffInSeconds / 86400);
      return t(days === 1 ? 'time.day_ago' : 'time.days_ago', { count: days });
    }
    return formatAppDate(date);
  };

  // Close panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNotificationClick = (notification) => {
    if (!notification.is_read) {
      markAsRead(notification.id);
    }
    
    if (notification.action_url) {
      navigate(notification.action_url);
      setIsOpen(false);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'security': return <Icon name="ShieldAlert" size={16} className="text-red-500" />;
      case 'system': return <Icon name="Settings" size={16} className="text-blue-500" />;
      case 'info':
      default: return <Icon name="Info" size={16} className="text-muted-foreground" />;
    }
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-muted-foreground hover:bg-muted hover:text-foreground rounded-lg transition-all focus:outline-none"
      >
        <Icon name="Bell" size={20} />
        {unreadCount > 0 && (
          <span className="absolute top-1.5 right-1.5 h-2.5 w-2.5 bg-red-500 rounded-full border-2 border-white shadow-sm flex items-center justify-center">
             <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
          </span>
        )}
      </button>
      {isOpen && (
        <div className="fixed left-1/2 -translate-x-1/2 md:absolute md:left-auto md:right-0 md:translate-x-0 top-16 md:top-auto mt-0 md:mt-2 w-[calc(100vw-2rem)] md:w-80 sm:w-96 bg-card border border-border rounded-xl shadow-2xl z-[200] overflow-hidden flex flex-col max-h-[calc(100vh-8rem)] md:max-h-[80vh]">
          <div className="p-4 border-b border-border flex justify-between items-center bg-background/50">
            <h3 className="font-bold text-foreground flex items-center">{t('notifications.title')}{unreadCount > 0 && (
                <span className="ml-2 bg-primary/20 text-primary text-xs font-bold px-2 py-0.5 rounded-full">
                  {t('notifications.new_count', { count: unreadCount })}
                </span>
              )}
            </h3>
            {notifications.length > 0 && unreadCount > 0 && (
              <button 
                onClick={markAllAsRead}
                className="text-xs font-medium text-primary hover:text-blue-800 transition-colors"
              >
                {t('notifications.mark_all_as_read')}
              </button>
            )}
          </div>
          
          <div className="overflow-y-auto flex-1 p-2 space-y-1">
            {notifications.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground flex flex-col items-center">
                <div className="w-12 h-12 bg-background rounded-full flex items-center justify-center mb-3">
                  <Icon name="BellOff" size={24} className="text-muted-foreground/70" />
                </div>
                <p className="text-sm font-medium">{t('notifications.none_yet')}</p>
                <p className="text-xs text-muted-foreground/70 mt-1">{t('notifications.none_subtitle')}</p>
              </div>
            ) : (
              notifications.slice(0, 5).map((notification) => (
                <div 
                  key={notification.id}
                  className={`group relative flex items-start p-3 rounded-lg transition-colors border border-transparent ${
                    !notification.is_read 
                      ? 'bg-primary/10/50 hover:bg-blue-50 border-primary/20/50' 
                      : 'hover:bg-background'
                  }`}
                >
                  <div 
                    className="flex-shrink-0 mt-0.5 mr-3 cursor-pointer"
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className={`p-2 rounded-full ${!notification.is_read ? 'bg-card shadow-sm' : 'bg-muted'}`}>
                      {getTypeIcon(notification.type)}
                    </div>
                  </div>
                  
                  <div 
                    className="flex-1 min-w-0 cursor-pointer"
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <p className={`text-sm ${!notification.is_read ? 'font-bold text-foreground' : 'font-medium text-foreground'}`}>
                      {notification.title}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                      {notification.message}
                    </p>
                    <p className="text-[10px] font-bold text-muted-foreground/70 mt-1.5 uppercase tracking-wider">
                      {formatTimeAgo(notification.created_at)}
                    </p>
                  </div>
                  
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      dismissNotification(notification.id);
                    }}
                    className="opacity-0 group-hover:opacity-100 absolute top-2 right-2 p-1.5 text-muted-foreground/70 hover:text-red-500 hover:bg-red-50 rounded-md transition-all"
                    title="Dismiss notification"
                  >
                    <Icon name="X" size={14} />
                  </button>
                  
                  {!notification.is_read && (
                    <div className="absolute top-1/2 -translate-y-1/2 left-0 w-1 h-8 bg-blue-500 rounded-r-full" />
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationPanel;
